class SampaPageIndex extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.links = Array.from(this.querySelectorAll('[data-side-nav-link]'));
    this.targets = this.links
      .map((link) => document.querySelector(link.getAttribute('href')))
      .filter(Boolean);

    this.links.forEach((link) => link.addEventListener('click', this.handleLink));
    document.addEventListener('click', this.handleOutsideClick);
    document.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('scroll', this.scheduleUpdate, { passive: true });
    window.addEventListener('resize', this.scheduleUpdate, { passive: true });
    this.scheduleUpdate();
  }

  handleOutsideClick = (event) => {
    if (this.classList.contains('is-open') && !this.contains(event.target)) this.close();
  };

  handleKeydown = (event) => {
    if (event.key !== 'Escape') return;
    this.close();
    if (this.contains(document.activeElement)) document.activeElement.blur();
  };

  handleLink = (event) => {
    const link = event.currentTarget;
    const target = document.querySelector(link.getAttribute('href'));
    const compact = window.matchMedia('(max-width: 1199px)').matches;

    if (compact && !this.classList.contains('is-open')) {
      event.preventDefault();
      this.open();
      return;
    }

    if (target) {
      event.preventDefault();
      const offset = this.getHeaderOffset();
      const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - offset);
      this.lockedTarget = target;
      this.lockUntil = performance.now() + 1800;
      this.setActive(target);
      window.scrollTo({
        top,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
      window.clearTimeout(this.unlockTimer);
      this.unlockTimer = window.setTimeout(() => {
        this.lockedTarget = null;
        this.scheduleUpdate();
      }, 1850);
    }

    this.close();
    link.blur();
  };

  getHeaderOffset() {
    const header = document.querySelector('sticky-header');
    if (!header) return 0;
    const position = window.getComputedStyle(header).position;
    if (position !== 'fixed' && position !== 'sticky') return 0;
    return Math.max(0, Math.round(header.getBoundingClientRect().height));
  }

  scheduleUpdate = () => {
    if (this.updateFrame) return;
    this.updateFrame = window.requestAnimationFrame(() => {
      this.updateFrame = null;
      this.updateActive();
    });
  };

  updateActive() {
    if (!this.targets.length) return;
    const headerOffset = this.getHeaderOffset();

    if (this.lockedTarget && performance.now() < this.lockUntil) {
      const distance = Math.abs(this.lockedTarget.getBoundingClientRect().top - headerOffset);
      if (distance > 8) {
        this.setActive(this.lockedTarget);
        return;
      }
      this.lockedTarget = null;
    }

    const marker = headerOffset + Math.min(180, window.innerHeight * .32);
    let active = this.targets[0];

    for (const target of this.targets) {
      if (target.getBoundingClientRect().top <= marker) active = target;
      else break;
    }

    if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2) {
      active = this.targets[this.targets.length - 1];
    }

    this.setActive(active);
  }

  setActive(target) {
    const activeHref = `#${target.id}`;
    this.links.forEach((link) => {
      const active = link.getAttribute('href') === activeHref;
      link.classList.toggle('is-active', active);
      if (active) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
  }

  open() {
    this.classList.add('is-open');
    this.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-expanded', 'false');
  }

  disconnectedCallback() {
    this.links.forEach((link) => link.removeEventListener('click', this.handleLink));
    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('scroll', this.scheduleUpdate);
    window.removeEventListener('resize', this.scheduleUpdate);
    window.cancelAnimationFrame(this.updateFrame);
    window.clearTimeout(this.unlockTimer);
  }
}

if (!customElements.get('sampa-side-nav')) customElements.define('sampa-side-nav', SampaPageIndex);
